import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router";
import { useEffect } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

export function ErrorPage() {
    const error = useRouteError();
    const navigate = useNavigate();

    // Chunk-load failures (stale deployment)
    const isChunkError =
        error instanceof Error &&
        (error.message.includes("Failed to fetch dynamically imported module") ||
            error.message.includes("Importing a module script failed"));

    // Auto-reload once on chunk error to get the latest version
    useEffect(() => {
        if (isChunkError) {
            const hasReloaded = sessionStorage.getItem("moniflow_error_reloaded");
            if (!hasReloaded) {
                sessionStorage.setItem("moniflow_error_reloaded", "true");
                window.location.reload();
            }
        } else {
            // Reset on normal errors or successful loads
            sessionStorage.removeItem("moniflow_error_reloaded");
        }
    }, [isChunkError]);

    const title = isRouteErrorResponse(error)
        ? error.status === 404
            ? "Page Not Found"
            : `Error ${error.status}`
        : isChunkError
            ? "Update Available"
            : "Something Went Wrong";

    const description = isRouteErrorResponse(error)
        ? error.status === 404
            ? "The page you're looking for doesn't exist or has been moved."
            : error.statusText || "An unexpected error occurred."
        : isChunkError
            ? "A new version of MoniFlow is available. Reload to get the latest update."
            : "An unexpected error occurred. Please try refreshing the page.";

    return (
        <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                {/* Logo */}
                <div className="flex items-center justify-center gap-2.5 mb-10">
                    <div className="w-10 h-10 rounded-xl bg-[#0B6E4F] flex items-center justify-center shadow-lg shadow-[#0B6E4F]/20">
                        <span className="text-white font-bold text-xl">M</span>
                    </div>
                    <span className="font-bold text-2xl text-[#0D1A12] tracking-tight">MoniFlow</span>
                </div>

                {/* Card */}
                <div className="bg-white rounded-3xl border border-[#E5E7EB] shadow-xl shadow-black/5 p-8">
                    <div className="w-16 h-16 bg-[#FFF7ED] rounded-2xl flex items-center justify-center mx-auto mb-5">
                        <AlertTriangle className="w-8 h-8 text-[#F59E0B]" />
                    </div>

                    <h1 className="text-xl font-bold text-[#0D1A12] mb-2">{title}</h1>
                    <p className="text-sm text-[#6B7280] mb-3 leading-relaxed">{description}</p>

                    {/* Debug info - only visible if status is 404 to help identify the mismatch source */}
                    {isRouteErrorResponse(error) && error.status === 404 && (
                        <p className="text-[10px] font-mono text-gray-400 mb-7">
                            Error Path: {window.location.pathname}
                        </p>
                    )}
                    {!isRouteErrorResponse(error) && error instanceof Error && (
                        <p className="text-[10px] font-mono text-red-400 mb-7">
                            Internal Error: {error.message}
                        </p>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                        {isChunkError ? (
                            <button
                                onClick={() => window.location.reload()}
                                className="flex-1 flex items-center justify-center gap-2 bg-[#0B6E4F] hover:bg-[#065F46] text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-lg shadow-[#0B6E4F]/20"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Reload Page
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex-1 flex items-center justify-center gap-2 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#374151] font-semibold py-3 px-4 rounded-xl transition-all"
                                >
                                    Go Back
                                </button>
                                <button
                                    onClick={() => navigate("/seller")}
                                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B6E4F] hover:bg-[#065F46] text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-lg shadow-[#0B6E4F]/20"
                                >
                                    <Home className="w-4 h-4" />
                                    Dashboard
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <p className="text-xs text-[#9CA3AF] mt-6">
                    © 2025 MoniFlow Technologies. All rights reserved.
                </p>
            </div>
        </div>
    );
}
